import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as authActions from 'actions/auth';
import logger from 'utils/logger';
import {
  PrimaryButton,
  FormattedError,
  Text,
  Link,
  Box,
  Heading,
  Panel,
  PanelContent,
  PanelWrapper,
  Metadata,
} from 'components';
import { InputField } from 'components/Forms';
import { useIsKeplerPlatform } from 'utils/hooks';
import ForgotPasswordSentView from './ForgotPasswordSentView';

function ForgotPasswordViewComponent() {
  const {
    createAccountEnabled,
    forgotPassword,
    register,
    loginEnabled,
  } = useSelector(({ settings, country }) => ({
    createAccountEnabled: country.isCreateAccountEnabled,
    forgotPassword: settings.features.loginMethod.forgotPassword,
    register: settings.features.register,
    loginEnabled: settings.loginEnabled,

  }));
  const dispatch = useDispatch();
  const [generalError, setGeneralError] = useState(null);
  const methods = useForm({ mode: 'onChange' });
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const { handleSubmit } = methods;
  const isKeplerPlatform = useIsKeplerPlatform();

  const submit = async (fields) => {
    setGeneralError(null);
    const response = await dispatch(authActions.requestPasswordChange(fields))
      .catch((err) => {
        logger.error('Password reset request failed', err);
        return null;
      });

    if (!response?.ok) {
      setGeneralError('forgotPassword.errFailed');
      return;
    }

    setSubmitSucceeded(true);
  };

  if (submitSucceeded) {
    return (
      <ForgotPasswordSentView
        createAccountEnabled={createAccountEnabled}
        forgotPassword={forgotPassword}
        register={register}
        loginEnabled={loginEnabled}
        isKeplerPlatform={isKeplerPlatform}
      />
    );
  }

  return (
    <PanelWrapper className="e2e-forgot-password">
      <Panel textAlign="center">
        <Metadata
          title={forgotPassword.title}
          description={forgotPassword.instructions}
        />

        <Heading mb="small">
          {forgotPassword.title}
        </Heading>

        <Heading fontSize="medium" mb="medium" as="h2">
          {forgotPassword.instructions}
        </Heading>
        <FormProvider {...methods}>
          <PanelContent centered as="form" onSubmit={handleSubmit(submit)} method="POST" noValidate>
            <InputField field={forgotPassword.inputField} />

            <FormattedError error={generalError} />

            <Box my="medium">
              <PrimaryButton variant="brand" type="submit">
                {/* MDM-11096 wrap text to avoid issues with Google Translate */}
                <span>
                  {forgotPassword.submitLabel}
                </span>
              </PrimaryButton>
            </Box>

            {loginEnabled && (
              <Box mb="fine">
                <Link
                  to={{ name: 'log-in' }}
                  id="logIn"
                  fontSize="small"
                />
              </Box>
            )}

            {createAccountEnabled && register && !isKeplerPlatform && (
              <Text
                id="noAccountCreateAccount"
                values={{
                  createAccountLink: (
                    <Link
                      key="createAccountLink"
                      to={{ name: 'create-account' }}
                      id="createAccount"
                    />
                  ),
                }}
                color="secondary"
                fontSize="small"
              />
            )}
          </PanelContent>
        </FormProvider>
      </Panel>
    </PanelWrapper>
  );
}

export default React.memo(ForgotPasswordViewComponent);
